import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { queryCache, useMutation } from "react-query";
import get from "lodash/get";

import {
  Dialog,
  Button,
  DraftFileUploader,
  showNotification,
  Loader,
  ApiErrorBanner,
  FormControlMessage,
  CheckboxGroup,
  TextInput,
} from "fe-shared/src/components";
import useProducts from "fe-shared/src/hooks/useProducts";
import useValidation from "fe-shared/src/hooks/useValidation";
import useMaterialsByCode from "fe-shared/src/hooks/useMaterialsByCode";
import useRecycleMaterials from "fe-shared/src/hooks/useRecycleMaterials";
import useNonEligibleMaterials from "fe-shared/src/hooks/useNonEligibleMaterials";
import { ApiError, FileInterface } from "fe-shared/src/types/common";
import {
  BusinessRole,
  MaterialCategoryName,
  MaterialUnit,
} from "fe-shared/src/types/domain";
import { ProcessingDirectionTitle, DetailsInfoTitle } from "..";

import { Transfer, TransferType, Address } from "../../types";
import { getFullAddress } from "../../helpers/address";
import {
  TRANSFERS_ACTIVITIES_QUERY_KEY,
  TRANSFERS_QUERY_KEY,
} from "../../consts";

import {
  getInitialContent,
  errorMessages,
  getValidationConfig,
} from "./modules/validation";
import {
  getCheckboxGroupProps,
  getMaterialsCountConfig,
} from "./modules/config";

import * as api from "./modules/api";

import styles from "./EditTransferDialog.module.scss";

interface Props {
  address?: Address;
  closeDialog: () => void;
  isDialogActive: boolean;
  transfer: Transfer;
  isStaffView?: boolean;
  isWithDocuments: boolean;
}

const EditTransferDialog = ({
  address,
  transfer,
  closeDialog,
  isDialogActive,
  isStaffView,
  isWithDocuments,
}: Props) => {
  const { t } = useTranslation();
  const {
    materials,
    transferId,
    transferType,
    transferNumber,
    nonEligibleMaterial,
  } = transfer;
  const { data: tires, isLoading: isTiresLoading } = useRecycleMaterials([
    BusinessRole.Hauler,
  ]);
  const { data: products, isLoading: isProductsLoading } = useProducts();
  const materialsByCode = useMaterialsByCode();
  const { data: nonEligibleMaterials } = useNonEligibleMaterials();

  const isWithWeight = materials[0]?.units.unit === MaterialUnit.Kg;
  const isWriteOff = [
    TransferType.NonEligibleWriteOff,
    TransferType.NonEligibleBufferWriteOff,
  ].includes(transferType);
  const isIntoProcessing = transferType === TransferType.IntoProcessing;
  const isDocumentRequired =
    isWithDocuments &&
    isWriteOff &&
    !!nonEligibleMaterials?.find(
      (m) => m.code === nonEligibleMaterial?.materialCode
    )?.isScaleTicketRequired;

  const initialContent = useMemo(() => getInitialContent(transfer), [transfer]);

  const validationConfig = useMemo(
    () => getValidationConfig(isDocumentRequired, isWithWeight, isWriteOff),
    [isDocumentRequired, isWithWeight, isWriteOff]
  );

  const {
    errors,
    values,
    isValid,
    validateAll,
    updateValue,
    validateValue,
    addArrayValue,
    removeArrayValue,
  } = useValidation(validationConfig, initialContent, errorMessages);

  const hasTires =
    materials[0]?.materialCategoryName === MaterialCategoryName.Tires;
  const isWithTires = isIntoProcessing || hasTires;
  const isWithProducts = isIntoProcessing || !hasTires;

  const tiresCheckboxGroupProps = useMemo(() => getCheckboxGroupProps(tires), [
    tires,
  ]);

  const productsCheckboxGroupProps = useMemo(
    () => getCheckboxGroupProps(products),
    [products]
  );

  const materialsCountConfig = useMemo(
    () =>
      getMaterialsCountConfig(materialsByCode, values.materials, isWithWeight),
    [materialsByCode, values, isWithWeight]
  );

  const tireTypeCodes = useMemo(
    () => values.materials.map((tire) => tire.materialCode),
    [values]
  );

  const onTiresChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    checkedValues: unknown[]
  ) => {
    if (checkedValues.length > tireTypeCodes.length) {
      const materialCode = checkedValues.find(
        (c) => !tireTypeCodes.includes(c as string)
      );
      const oldValue = materials.find((i) => i.materialCode === materialCode)
        ?.units.unitQuantity;
      const {
        units: { unit },
      } = materials[0];

      addArrayValue("materials", {
        materialCode,
        units: {
          unit,
          unitQuantity: oldValue || 0,
        },
      });
    } else {
      const index = tireTypeCodes.findIndex(
        (tireCode) => !checkedValues.includes(tireCode)
      );

      removeArrayValue("materials", index);
    }
  };

  const getOriginalMaterialQuantity = (code: string) =>
    materials.find((material) => material.materialCode === code)?.units
      .unitQuantity;

  const updateNumberValue = (key: string, value: string | number) => {
    const valueNumber = Number(value);
    const isCorrectValue = !Number.isNaN(valueNumber);
    const savedValue = isCorrectValue ? valueNumber : value;

    updateValue(key, savedValue, true);
  };

  const [updateTransfer, mutationResult] = useMutation<
    unknown,
    AxiosError<ApiError>,
    FileInterface[]
  >(
    () =>
      api.updateTransfer({
        transferNumber,
        content: values,
        transferType,
        transferId,
        isStaff: isStaffView,
      }),
    {
      throwOnError: true,
      onSuccess: () => {
        queryCache.invalidateQueries([TRANSFERS_QUERY_KEY, transferNumber]);
        queryCache.invalidateQueries([
          TRANSFERS_ACTIVITIES_QUERY_KEY,
          transferNumber,
        ]);
      },
    }
  );

  const onSubmit = async () => {
    if (isValid) {
      await updateTransfer();
      closeDialog();
      showNotification(t("transfers.details.modalSuccessToast"));
    } else {
      validateAll();
    }
  };

  const title = (
    <ProcessingDirectionTitle
      from={t(`transfers.details.modalTitle.${transferType}.from`)}
      to={t(`transfers.details.modalTitle.${transferType}.to`)}
    />
  );

  const dialogTitle = (
    <DetailsInfoTitle
      title={title}
      subTitle={getFullAddress(address)}
      className={styles.dialogTitle}
    />
  );

  const dialogActions = [
    <Button
      key="1"
      onClick={onSubmit}
      id="transfer-dialog-submit-btn"
      disabled={!!Object.keys(errors).length}
    >
      {t(`transfers.details.dialog.successBtn`)}
    </Button>,
    <Button
      key="2"
      variant="outlined"
      onClick={closeDialog}
      id="transfer-dialog-cancel-btn"
    >
      {t("transfers.details.dialog.cancelBtn")}
    </Button>,
  ];

  const isLoading =
    mutationResult.isLoading || isTiresLoading || isProductsLoading;

  const countTitle = `transfers.details.dialog.materials${
    isWithWeight ? "Weight" : "Count"
  }Title`;

  return (
    <Dialog
      modal
      width={620}
      title={dialogTitle}
      onClose={closeDialog}
      actions={dialogActions}
      isActive={isDialogActive}
    >
      {isLoading && <Loader isBlocking />}
      {mutationResult.isError && (
        <ApiErrorBanner
          className={styles.banner}
          requestStatus={mutationResult}
          id="transfer-dialog-error-banner"
        />
      )}
      {isStaffView && !isWriteOff && (
        <>
          {isWithTires && (
            <>
              <h4 className={styles.modalSubHeader}>
                {t("transfers.details.dialog.tiresTitle")}
              </h4>
              <CheckboxGroup
                id="materials"
                value={tireTypeCodes}
                onChange={onTiresChange}
                isError={!!errors.materials}
                errorText={errors.materials}
                {...tiresCheckboxGroupProps}
              />
            </>
          )}
          {isWithProducts && (
            <>
              <h4 className={styles.modalSubHeader}>
                {t("transfers.details.dialog.productsTitle")}
              </h4>
              <CheckboxGroup
                id="materials"
                value={tireTypeCodes}
                onChange={onTiresChange}
                isError={!!errors.materials}
                errorText={errors.materials}
                {...productsCheckboxGroupProps}
              />
            </>
          )}
          {!!materialsCountConfig.length && (
            <>
              <h4 className={styles.modalSubHeader}>{t(countTitle)}</h4>
              {materialsCountConfig.map(
                ({ valueKey, componentProps }, index) => {
                  const { title: tireTitle, ...otherProps } = componentProps;

                  return (
                    <div key={valueKey} className={styles.tiresCount}>
                      <div className={styles.tiresCountTitle}>{tireTitle}</div>
                      <TextInput
                        id={valueKey}
                        errorText={errors[valueKey]}
                        isError={!!errors[valueKey]}
                        value={get(values, valueKey) || ""}
                        {...otherProps}
                        onBlur={() => validateValue(valueKey)}
                        onChange={(e, value) => {
                          updateNumberValue(valueKey, value);
                        }}
                      />
                      <span className={styles.tiresCountOriginal}>
                        {getOriginalMaterialQuantity(
                          values.materials[index].materialCode
                        )}
                      </span>
                    </div>
                  );
                }
              )}
            </>
          )}
        </>
      )}
      {isWithDocuments && (
        <>
          <h4 className={styles.modalSubHeader}>
            {t("transfers.details.dialog.title")}
          </h4>
          <FormControlMessage
            id="attachments"
            className={styles.error}
            errorText={t("validation.documentsRequired")}
            isError={
              !!errors.attachments &&
              errors.attachments !== `"attachments" is not allowed`
            }
          />
          <DraftFileUploader
            files={values.attachments}
            path="transactions/attachment"
            onChange={(files) => updateValue("attachments", files, true)}
          />
        </>
      )}
    </Dialog>
  );
};

export default EditTransferDialog;
